// The summary generator emits a monotone contour made only of M, L and C.
// Match its rounded shoulders instead of interpolating incompatible SVG strings.
type Point = [number, number];
type Shoulder = { points: [Point, Point, Point, Point]; direction: number };
export type SummaryContour = { startY: number; shoulders: Shoulder[]; endY: number; width: number };
export type SummaryMorph = { from: SummaryContour; to: SummaryContour };

export function readSummaryContour(stroke: string): SummaryContour {
  let cursor: Point = [0, 0];
  let startY = 0;
  const shoulders: Shoulder[] = [];
  for (const match of stroke.matchAll(/([MLC])([^MLC]+)/g)) {
    const values = match[2]!.trim().split(/[ ,]+/).map(Number);
    if (match[1] === "C") {
      const end: Point = [values[4]!, values[5]!];
      shoulders.push({
        points: [cursor, [values[0]!, values[1]!], [values[2]!, values[3]!], end],
        direction: Math.sign(end[1] - cursor[1]),
      });
      cursor = end;
    } else {
      cursor = [values[0]!, values[1]!];
      if (match[1] === "M") startY = cursor[1];
    }
  }
  return { startY, shoulders, endY: cursor[1], width: cursor[0] };
}

const center = (shoulder: Shoulder) => (shoulder.points[0][0] + shoulder.points[3][0]) / 2;
const collapsed = (x: number, y: number, direction: number): Shoulder => ({
  points: [
    [x, y],
    [x, y],
    [x, y],
    [x, y],
  ],
  direction,
});

/** Add zero-width shoulders between real ones; the original contour is unchanged. */
function padShoulders(shoulders: Shoulder[], opposite: Shoulder[], before: Point, after: Point): Shoulder[] {
  if (shoulders.length >= opposite.length) return shoulders;
  const size = Math.max(shoulders.length, opposite.length);
  const placed = new Map<number, Shoulder>();
  shoulders.forEach((shoulder, index) => {
    const position =
      shoulders.length === 1 ? Math.floor((size - 1) / 2) : Math.round((index * (size - 1)) / (shoulders.length - 1));
    placed.set(position, shoulder);
  });
  // A new valley starts at its own center, rather than changing an entire plateau.
  const emptyX = Math.max(
    before[0],
    Math.min(after[0], (opposite[0]!.points[0][0] + opposite.at(-1)!.points[3][0]) / 2),
  );
  const positions = [...placed.keys()];
  let nextPosition = 0;
  let previous = before;
  return Array.from({ length: size }, (_, index) => {
    const shoulder = placed.get(index);
    if (shoulder) {
      nextPosition += 1;
      previous = shoulder.points[3];
      return shoulder;
    }
    const next = placed.get(positions[nextPosition]!)?.points[0] ?? after;
    const x = shoulders.length ? (previous[0] + next[0]) / 2 : emptyX;
    return collapsed(x, previous[1], opposite[index]?.direction ?? 0);
  });
}

/** Match nearby shoulders of the same direction, preserving the order of the contour. */
export function prepareSummaryMorph(from: SummaryContour, to: SummaryContour): SummaryMorph {
  const nearest = (source: Shoulder[], target: Shoulder[]) => {
    const byDirection = new Map<number, number[]>();
    target.forEach((shoulder, index) => {
      const indices = byDirection.get(shoulder.direction) ?? [];
      indices.push(index);
      byDirection.set(shoulder.direction, indices);
    });
    return source.map((shoulder) => {
      const indices = byDirection.get(shoulder.direction) ?? [];
      const x = center(shoulder);
      let low = 0;
      let high = indices.length;
      while (low < high) {
        const middle = (low + high) >>> 1;
        if (center(target[indices[middle]!]!) < x) low = middle + 1;
        else high = middle;
      }
      const candidates = indices.slice(Math.max(0, low - 1), low + 1);
      return candidates.reduce(
        (best, index) =>
          best < 0 || Math.abs(center(target[index]!) - x) < Math.abs(center(target[best]!) - x) ? index : best,
        -1,
      );
    });
  };
  const forward = nearest(from.shoulders, to.shoulders);
  const backward = nearest(to.shoulders, from.shoulders);
  const paired: [number, number][] = [];
  forward.forEach((target, source) => {
    if (target >= 0 && backward[target] === source && target > (paired.at(-1)?.[1] ?? -1))
      paired.push([source, target]);
  });
  const fromShoulders: Shoulder[] = [];
  const toShoulders: Shoulder[] = [];
  let fromIndex = 0;
  let toIndex = 0;
  paired.push([from.shoulders.length, to.shoulders.length]);
  for (const [nextFrom, nextTo] of paired) {
    const fromGroup = from.shoulders.slice(fromIndex, nextFrom);
    const toGroup = to.shoulders.slice(toIndex, nextTo);
    if (fromGroup.length || toGroup.length) {
      fromShoulders.push(
        ...padShoulders(
          fromGroup,
          toGroup,
          from.shoulders[fromIndex - 1]?.points[3] ?? [0, from.startY],
          from.shoulders[nextFrom]?.points[0] ?? [from.width, from.endY],
        ),
      );
      toShoulders.push(
        ...padShoulders(
          toGroup,
          fromGroup,
          to.shoulders[toIndex - 1]?.points[3] ?? [0, to.startY],
          to.shoulders[nextTo]?.points[0] ?? [to.width, to.endY],
        ),
      );
    }
    if (nextFrom < from.shoulders.length && nextTo < to.shoulders.length) {
      fromShoulders.push(from.shoulders[nextFrom]!);
      toShoulders.push(to.shoulders[nextTo]!);
    }
    fromIndex = nextFrom + 1;
    toIndex = nextTo + 1;
  }
  return { from: { ...from, shoulders: fromShoulders }, to: { ...to, shoulders: toShoulders } };
}

export function interpolateSummary(morph: SummaryMorph, progress: number): SummaryContour {
  const { from, to } = morph;
  const mix = (a: number, b: number) => a + (b - a) * progress;
  return {
    startY: mix(from.startY, to.startY),
    endY: mix(from.endY, to.endY),
    width: mix(from.width, to.width),
    shoulders: from.shoulders.map((shoulder, index) => {
      const points = shoulder.points.map((point, vertex) => {
        const target = to.shoulders[index]!.points[vertex]!;
        return [mix(point[0], target[0]), mix(point[1], target[1])];
      }) as Shoulder["points"];
      return { direction: Math.sign(points[3][1] - points[0][1]) || to.shoulders[index]!.direction, points };
    }),
  };
}

export function summaryContourPaths(contour: SummaryContour, baseY: number): { fill: string; stroke: string } {
  const inner = contour.shoulders
    .map(
      ({ points: [start, first, second, end] }) =>
        `L${start.join(" ")}C${first.join(" ")} ${second.join(" ")} ${end.join(" ")}`,
    )
    .join("");
  const top = `L0 ${contour.startY}${inner}L${contour.width} ${contour.endY}`;
  return {
    stroke: `M0 ${contour.startY}${inner}L${contour.width} ${contour.endY}`,
    fill: `M0 ${baseY}${top}L${contour.width} ${baseY}Z`,
  };
}
